import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useCurrentEditor } from '@tiptap/react';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import MenuList from '@components/MenuList';
import { useTranslation, useSurveysOptions } from '@hooks';
import { addTipTapMarkTag } from '@helpers/text-editor';
import { InboxSurveysIcon } from 'assets/images/icons';

const SurveysMenu = ({ disabled, setMessage, activeConversation }) => {
    const { t } = useTranslation();
    const { editor } = useCurrentEditor();
    const isMobile = useMediaQuery('@media (max-width: 600px)');

    const [anchorElMain, setAnchorElMain] = useState(null);
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

    const companies = useSelector(state => state.account?.account?.companies || []);
    const { getBrandSurveysOptionsByIds } = useSurveysOptions();

    useEffect(() => {
        editor?.commands.clearContent();
    }, [activeConversation?.id]);

    const handleOpenMain = event => {
        if (isMobile) {
            setMobileMenuOpen(true);
        } else {
            setAnchorElMain(event.currentTarget);
        }
    };

    const handleCloseMain = () => setAnchorElMain(null);

    const onClickTemplate = survey => {
        const locationName =
            activeConversation?.locations?.length > 1
                ? ''
                : companies.find(c => c.companyId === activeConversation?.locations[0])?.locationName;
        const newMessage = survey.message
            ?.replace(/\[name\]/g, activeConversation?.firstName || '')
            ?.replace(/\[last_name\]/g, activeConversation?.lastName || '')
            ?.replace(/\[location\]/g, locationName);
        const placeholderWithId = `[survey:${survey.id}]`;
        const messageForEditor = `${newMessage ? `${newMessage} ` : ''}${placeholderWithId}`;
        editor.commands.setContent(addTipTapMarkTag(messageForEditor));
        setMessage(JSON.stringify(editor?.getJSON() || {}));
        setMobileMenuOpen(false);
        setAnchorElMain(null);
    };

    const mapSurveyPropsToOption = survey => ({
        value: survey.id,
        icon: null,
        text: survey.name,
        onClick: () => onClickTemplate(survey),
    });

    const mapMenu = useMemo(() => {
        return getBrandSurveysOptionsByIds(activeConversation?.locations, mapSurveyPropsToOption);
    }, [companies, activeConversation]);

    return (
        <>
            <MenuList
                options={mapMenu}
                anchorEl={anchorElMain}
                onCloseMenu={handleCloseMain}
                openModal={isMobileMenuOpen}
                handleCloseModal={() => setMobileMenuOpen(false)}
                mainTitle={t('common.surveys')}
                menuButton={
                    <IconButton
                        size="small"
                        color="inherit"
                        onClick={handleOpenMain}
                        disabled={disabled}
                        sx={{ height: 18, width: 18, padding: '2px !important' }}
                    >
                        <InboxSurveysIcon />
                    </IconButton>
                }
            />
        </>
    );
};

export default SurveysMenu;
